import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { OrgUserRequest, OrgUserResponse } from '../../../@types/organizationUser/organizationUser.type';
import axiosInstance from '../../../utils/axios';

interface RequestState<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface OrgUserState {
  single: RequestState<OrgUserResponse>;
  list: RequestState<OrgUserResponse[]>;
  create: RequestState<OrgUserResponse>;
  update: RequestState<OrgUserResponse>;
  delete: RequestState<string>;
}

const initialRequestState = <T,>(): RequestState<T> => ({
  data: null,
  loading: false,
  error: null,
});

const initialState: OrgUserState = {
  single: initialRequestState<OrgUserResponse>(),
  list: initialRequestState<OrgUserResponse[]>(),
  create: initialRequestState<OrgUserResponse>(),
  update: initialRequestState<OrgUserResponse>(),
  delete: initialRequestState<string>(),
};

const BASE_URL = '/api/v1/organization-users';

// ---------- Thunks ----------
export const fetchOrgUserById = createAsyncThunk(
  'orgUsers/fetchByUserId',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`${BASE_URL}/${id}`);
      return res.data as OrgUserResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch failed');
    }
  }
);

export const fetchOrgUsers = createAsyncThunk(
  'orgUsers/fetchByOrgId',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/api/v1/organizations/${id}/users`);
      return res.data.content as OrgUserResponse[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch failed');
    }
  }
);


export const createOrgUser = createAsyncThunk(
  'orgUsers/create',
  async (data: OrgUserRequest, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(BASE_URL, data);
      return res.data as OrgUserResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Create failed');
    }
  }
);

export const updateOrgUser = createAsyncThunk(
  'orgUsers/update',
  async ({ id, data }: { id: string; data: OrgUserRequest }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`${BASE_URL}/${id}`, data);
      console.log('UPDATED USER: ', res)
      return res.data as OrgUserResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Update failed');
    }
  }
);

export const deleteOrgUser = createAsyncThunk(
  'orgUsers/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/${id}`);
      console.log('org user deleted succ', id)
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Delete failed');
    }
  }
);

// ---------- Slice ----------
const orgUserSlice = createSlice({
  name: 'orgUsers',
  initialState,
  reducers: {
    clearCreateOrgUserState: (state) => {
      state.create = initialRequestState();
    },
    clearUpdateOrgUserState: (state) => {
      state.update = initialRequestState();
    },
    clearDeleteOrgUserState: (state) => {
      state.delete = initialRequestState();
    },
  },
  extraReducers: (builder) => {
    // Fetch user by ID
    builder.addCase(fetchOrgUserById.pending, (state) => {
      state.single.loading = true;
      state.single.error = null;
    });
    builder.addCase(fetchOrgUserById.fulfilled, (state, action) => {
      state.single.loading = false;
      state.single.data = action.payload;
    });
    builder.addCase(fetchOrgUserById.rejected, (state, action) => {
      state.single.loading = false;
      state.single.error = action.payload as string;
    });

    // Fetch users by org ID
    builder.addCase(fetchOrgUsers.pending, (state) => {
      state.list.loading = true;
      state.list.error = null;
    });
    builder.addCase(fetchOrgUsers.fulfilled, (state, action) => {
      state.list.loading = false;
      state.list.data = action.payload;
    });
    builder.addCase(fetchOrgUsers.rejected, (state, action) => {
      state.list.loading = false;
      state.list.error = action.payload as string;
    });

    // Create
    builder.addCase(createOrgUser.pending, (state) => {
      state.create.loading = true;
      state.create.error = null;
    });
    builder.addCase(createOrgUser.fulfilled, (state, action) => {
      state.create.loading = false;
      state.create.data = action.payload;
      if (state.list.data) {
        state.list.data.push(action.payload);
      }
    });
    builder.addCase(createOrgUser.rejected, (state, action) => {
      state.create.loading = false;
      state.create.error = action.payload as string;
    });

    // Update
    builder.addCase(updateOrgUser.pending, (state) => {
      state.update.loading = true;
      state.update.error = null;
    });
    builder.addCase(updateOrgUser.fulfilled, (state, action) => {
      state.update.loading = false;
      state.update.data = action.payload;
      if (state.list.data) {
        const index = state.list.data.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.list.data[index] = action.payload;
        }
      }
    });
    builder.addCase(updateOrgUser.rejected, (state, action) => {
      state.update.loading = false;
      state.update.error = action.payload as string;
    });

    // Delete
    builder.addCase(deleteOrgUser.pending, (state) => {
      state.delete.loading = true;
      state.delete.error = null;
    });
    builder.addCase(deleteOrgUser.fulfilled, (state, action) => {
      state.delete.loading = false;
      state.delete.data = action.payload;
    });
    builder.addCase(deleteOrgUser.rejected, (state, action) => {
      state.delete.loading = false;
      state.delete.error = action.payload as string;
    });
  },
});

export const {
  clearCreateOrgUserState,
  clearUpdateOrgUserState,
  clearDeleteOrgUserState,
} = orgUserSlice.actions;

export default orgUserSlice.reducer;
